import AriaControl from "../../../utilities/accessibility/js/AriaControl";
import { _defaultConfigOptions } from "../../../utilities/accessibility/js/constants";
import MainNavigation from "./MainNavigation";
/**
 * MainNavigationControl is used to initialise a navigation control
 * that looks for the control element as well as any elements it should toggle
 * Indicated by the data attributes:
 *  [data-control='id']
 *  [data-controlled-by='id|id']
 *
 * 'data-controlled-by' being a pipe separated value of ids, so that many controls
 * can control the same component.
 *
 * We also consider the case where our containing menu should remain open while
 * we toggle content, so have a tertiary check for:
 *  [data-control-menu]
 *
 * An optional parameter isClose is used to determine if the button should function
 * as just a close button.
 */
export default class MainNavigationControl extends AriaControl {
  /**
   * Default config that we control the ARIA attributes with
   */
  static defaultConfigOptions = {
    ..._defaultConfigOptions,
    menu: undefined,
    controls: undefined,
  };

  /**
   * Construct the control and set properties from the config
   * Extended configO
   *
   * @param {Object} configOptions
   */
  constructor(configOptions) {
    // Get the required options to construct control
    const {
      element,
      page,
      root,
      menu,
      header,
      controls,
      allTargets,
      expandable,
      expanded,
    } = configOptions;
    // AriaControl will handle initial ARIA configuration
    super({ control: element, expandable, expanded });
    this.configOptions = configOptions;
    // Control configuration
    this.control = element;
    this.page = page;
    this.root = root;
    this.menu = menu;
    this.header = header;
    this.controls = controls;
    this.allTargets = allTargets;
    // Determine if this button is a close button
    this.id = this.control.id;
    this.classList = this.control.classList;
    this.isClose = /close/gi.exec(this.id);
    this.targets = this.menu.querySelectorAll(
      `[data-controlled-by*=${this.id}]`,
    );
    // Add event listeners
    this.control.addEventListener("click", this.handleClick.bind(this));

    // update tab index
    this.handleTabIndex();
  }

  /**
   * Toggle the provided element
   * Expanded state can be specified manually
   *
   * @param {defaultConfigOptions} configOptions
   */
  toggleElement(element, expanded = undefined, modifyAria = false) {
    // Get static variables
    const activeClass = MainNavigation.activeClass;
    const lockedClass = MainNavigation.lockedClass;

    // Switch statement because we also want to have a default behaviour
    switch (!!expanded) {
      case true:
        element.classList.add(activeClass);
        this.root.classList.add(activeClass);
        this.page.classList.add(lockedClass);
        this.addBreakout();
        setTimeout(() => {
          // Hack to circumvent other focusTrap toggles
          MainNavigation.focusTrap.activate();
        }, 10);
        break;
      case false:
        element.classList.remove(activeClass);
        this.root.classList.remove(activeClass);
        this.page.classList.remove(lockedClass);
        MainNavigation.focusTrap.deactivate();
        break;
      default:
        element.classList.toggle(activeClass);
        this.root.classList.toggle(activeClass);
        this.page.classList.toggle(lockedClass);
        if (MainNavigation.focusTrap.active) {
          setTimeout(() => {
            // Hack to circumvent other focusTrap toggles
            MainNavigation.focusTrap.activate();
          }, 10);
        } else {
          MainNavigation.focusTrap.deactivate();
        }
        break;
    }

    if (modifyAria) {
      // Modify the ARIA attributes alongside the element
      this.modifyAria({
        ...this.configOptions,
        target: element,
        expanded,
        action: "expand",
      });
    }
  }

  // close menu
  closeMenu() {
    const setActiveTarget = MainNavigation.setActiveTarget;
    // We're closing the menu
    this.toggleMenu(false);
    // Deactivate all controls
    this.toggleAllControls(false);
    this.targets.forEach((target) => {
      this.toggleTarget(target, false);
    });
    setActiveTarget("");
  }

  // callback to detect outside click
  breakoutCallback(event) {
    if (
      !this.menu.contains(event.target) &&
      !this.header.contains(event.target)
    ) {
      this.closeMenu();
    }
    // run once only
    this.page.removeEventListener("click", (event) => {
      this.breakoutCallback(event);
    });
  }

  // add the breakout method
  addBreakout() {
    this.page.addEventListener("click", (event) => {
      this.breakoutCallback(event);
    });
  }

  /**
   * Dedicated menu handler
   * Toggles just the menu element
   *
   * @param {Boolean} expanded
   */
  toggleMenu(expanded = undefined) {
    // Toggle (and modifyAria)
    this.toggleElement(this.menu, expanded, true);
  }

  /**
   * Dedicated control handler
   * Toggles just the control
   *
   * @param {Boolean} expanded
   */
  toggleControl(expanded = undefined) {
    this.toggleElement(this.control, expanded);
  }

  /**
   * We toggle all controls off which are all MainNavigationControl classes
   * and will have the toggleControl() function
   *
   * @param {Boolean} isOpen
   */
  toggleAllControls(isOpen = undefined) {
    this.controls.forEach((control) => {
      control.toggleControl(isOpen);
    });
  }

  /**
   * Dedicated target handler
   * Toggles the target with a transition
   *
   * @param {HTMLDivElement} target
   * @param {Boolean} expanded
   */
  toggleTarget(target, expanded = undefined) {
    // Get static properties
    const transitionClass = MainNavigation.transitionClass;

    // Cancel any current transition timeout event
    clearTimeout(target.transitionTimeout);

    // Add the transition class, have it removed at the end of transition
    // Current transition time based on styles is 0.2s = 200ms
    target.classList.add(transitionClass);

    // Add a timeout event to remove the class at the end of the transition
    target.transitionTimeout = setTimeout(() => {
      target.classList.remove(transitionClass);
    }, 300);

    // Timeout hack to avoid glitchy transition when menu is already open
    setTimeout(() => {
      // Toggle with ARIA
      this.toggleElement(target, expanded, true);
    }, 10);
  }

  handleTabIndex() {
    const mobile = this.control.getAttribute("data-tabindex-mobile");
    const desktop = this.control.getAttribute("data-tabindex-desktop");
    if (
      window.squiz.breakpoints.lg.matches ||
      window.squiz.breakpoints.xl.matches
    ) {
      if (mobile)
        this.control.setAttribute(
          "tabindex",
          this.control.getAttribute("data-tabindex-desktop"),
        );
    } else {
      if (desktop)
        this.control.setAttribute(
          "tabindex",
          this.control.getAttribute("data-tabindex-mobile"),
        );
    }
  }
  /**
   * The function of this click is fairly simple, we only want to
   * toggle the '.active' class, or if the current active menu item
   * isn't ours, display the relevant menu without hiding the main menu
   */
  handleClick() {
    // Get static properties
    const activeTarget = MainNavigation.activeTarget;
    const setActiveTarget = MainNavigation.setActiveTarget;

    if (!activeTarget && !this.isClose) {
      // Menu isn't open, toggle active
      this.toggleMenu(true);
      this.toggleControl(true);
      this.targets.forEach((target) => {
        this.toggleTarget(target, true);
      });
      setActiveTarget(this.id);
    } else if (activeTarget === this.id || this.isClose) {
      this.closeMenu();
    } else {
      // We're switching menus, toggle the content
      // Deactivate all controls
      this.toggleAllControls(false);
      // Toggle the current one back on
      this.toggleControl(true);
      this.allTargets.forEach((target) => {
        this.toggleTarget(target, false);
      });
      // Now toggle all of the current controls targets
      this.targets.forEach((target) => {
        this.toggleTarget(target, true);
      });
      setActiveTarget(this.id);
    }
  }
}
